import { inject, Injectable, InjectionToken } from '@angular/core';

// TODO(ds): imports between cosmos/config and cosmos/analytics should be refactored
// eslint-disable-next-line @nx/enforce-module-boundaries
import { injectConfigProperty } from '@cosmos/config';
import { getZoneUnPatchedApi, unpatchedApis } from '@cosmos/zone-less';

// Separate token for the URL so it can be overridden in tests or Storybook
// without having to replace the entire sender implementation.
export const ASI_STATS_SENDER_FETCH_URL = new InjectionToken<string>('', {
  factory: () => injectConfigProperty('asiStats').url,
});

// Separate token for the fetch implementation so it can be swapped out in tests
// to intercept outgoing requests without mocking the global fetch.
// Uses the zone-unpatched fetch to prevent Angular's zone.js from tracking
// these requests as async tasks, which would otherwise delay change detection
// completion and cause unnecessary re-renders.
export const ASI_STATS_SENDER_FETCH_IMPL = new InjectionToken<
  (requestUrl: string, init: RequestInit) => Promise<Response>
>('', {
  factory: () => {
    const nativeFetch = getZoneUnPatchedApi('fetch');
    return (requestUrl: string, init: RequestInit) =>
      nativeFetch(requestUrl, init);
  },
});

@Injectable({ providedIn: 'root' })
export class AsiStatsSender {
  private readonly _retryCount: number;
  private readonly _retryDelay: number;

  private readonly _url = inject(ASI_STATS_SENDER_FETCH_URL);
  private readonly _fetchImpl = inject(ASI_STATS_SENDER_FETCH_IMPL);

  constructor() {
    const { retry } = injectConfigProperty('asiStats');
    // Fall back to sensible defaults so stats delivery works even if the
    // retry config is missing or partially defined in the environment config.
    this._retryCount = retry?.count ?? 3;
    this._retryDelay = retry?.delay ?? 2000;
  }

  sendStats(ipAddress: string | null, eventPayloads: object[]): void {
    const fetchRetry = async () => {
      // Start at _retryCount and count down to 0, giving _retryCount + 1
      // total attempts (1 initial attempt + _retryCount retries).
      let count = this._retryCount;
      while (count >= 0) {
        try {
          const headers: HeadersInit = {
            accept: 'application/json',
            'content-type': 'application/json',
          };

          // Forward the client IP so the stats backend can attribute events
          // to the correct user even when requests are proxied through the app server.
          if (ipAddress) {
            headers['x-client-ip'] = ipAddress;
          }

          // We only provide the request init object since url is provided through the `ASI_STATS_SENDER_FETCH_URL`.
          const response = await this._fetchImpl(this._url, {
            method: 'POST',
            headers,
            body: JSON.stringify(eventPayloads),
          });

          if (!response.ok) {
            // Not consuming a non-ok body can leave the connection open.
            response.body?.cancel();
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          // ✅ Success - break out of retry loop
          return;
        } catch {
          // Stats delivery failures are non-critical and expected in degraded
          // network conditions, so we intentionally avoid reporting them to
          // Rollbar to prevent alert noise.
          // Use zone-unpatched Promise and setTimeout to avoid triggering
          // change detection cycles during the retry delay.
          await new unpatchedApis.Promise((resolve) =>
            unpatchedApis.setTimeout(resolve, this._retryDelay)
          );
          count -= 1;
        }
      }
    };

    // Fire-and-forget — stats delivery is best-effort and should never block
    // the caller or surface errors to the user.
    fetchRetry();
  }
}
